import { useMemo, useState, useEffect } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import PageHeader from '../components/PageHeader';
import { useText } from '../context/TextContext';
import TimesheetEntryModal from '../components/modals/TimesheetEntryModal';
import CreateInvoiceModal from '../components/modals/CreateInvoiceModal';
import { DataTable, DataTableColumn } from '../components/DataTable';
import { useFirebaseQuery, useFirebaseQueryOptions } from '../hooks/firebaseHooks';
import { TimeRecord } from '../models/shared/TimeRecord';
import { Article } from '../models/shared/Article';
import { Deal } from '../models/shared/Deal';
import { COLLECTIONS, PATHS } from '../utils/shared/constants';
import { sortArticles } from '../utils/shared/article';
import { useOrganization } from '../context/OrganizationContext';
import DealLink from '../components/DealLink';
import Gravatar from '../components/Gravatar';
import { formatDurationFromMs, formatDuration, formatDate } from '../utils/date';
import { where, or, doc, deleteDoc, getDoc } from 'firebase/firestore';
import OrgMemberSelect from '../components/OrgMemberSelect';
import { useAuth, useFirestore } from 'reactfire';
import { AdvancedButton } from '../components/AdvancedButton';
import useConfirmModal from '../hooks/useConfirmModal';
import { toast } from 'react-toastify';
import {getDateRangeForFilter, buildTimesheetColumns, buildTimesheetTable, generateTimesheetCsv, UNASSIGNED_ACTIVITY_KEY, TimePeriodFilter, GroupBy, ColumnGrouping, TimesheetRow} from '../utils/timesheet';

interface CustomDateForm {
    startDate: string;
    endDate: string;
}

export default function TimeSheetsPage() {
    const text = useText();
    const { orgId, orgRole } = useOrganization();
    const [showNewEntryModal, setShowNewEntryModal] = useState(false);
    const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState<TimePeriodFilter>('last7days');
    const [selectedCell, setSelectedCell] = useState<{key: string; columnIndex: number} | null>(null);
    const [editingRecord, setEditingRecord] = useState<TimeRecord | null>(null);
    const [groupBy, setGroupBy] = useState<GroupBy>('deal');
    const [columnGrouping, setColumnGrouping] = useState<ColumnGrouping>('day');
    const firestore = useFirestore();
    const [ConfirmModal, confirm] = useConfirmModal(text.deleteTimeRecord);
    const isManager = orgRole !== 'worker';
    const { currentUser } = useAuth();
    const [email, setEmail] = useState(isManager ? '' : currentUser?.email || '');

    const [activities] = useFirebaseQuery<Article>(
        `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.ARTICLES}`,
        where('status', '==', 'active'),
        where('isActivity', '==', true),
    );

    const { register, watch, setValue } = useForm<CustomDateForm>();
    const watchedStartDate = watch('startDate');
    const watchedEndDate = watch('endDate');

    const isDateRangeValid = useMemo(() => {
        if (selectedFilter !== 'custom' || !watchedStartDate || !watchedEndDate) return true;
        return new Date(watchedEndDate) >= new Date(watchedStartDate);
    }, [selectedFilter, watchedStartDate, watchedEndDate]);

    const { startDate, endDate } = useMemo(() => {
        if (selectedFilter === 'custom' && watchedStartDate && watchedEndDate) {

            const [startYear, startMonth, startDay] = watchedStartDate.split('-').map(Number);
            const localStartDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);

            const [endYear, endMonth, endDay] = watchedEndDate.split('-').map(Number);
            const localEndDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);

            return { startDate: localStartDate, endDate: localEndDate };
        }
        return getDateRangeForFilter(selectedFilter);
    }, [selectedFilter, watchedStartDate, watchedEndDate]);

    useEffect(() => {
        if (selectedFilter !== 'custom') {
            const { startDate: rangeStart, endDate: rangeEnd } = getDateRangeForFilter(selectedFilter);
            const startString = formatDate(rangeStart, { isoDate: true, noTime: true });
            const endString = formatDate(rangeEnd, { isoDate: true, noTime: true });

            setValue('startDate', startString);
            setValue('endDate', endString);
        }
    }, [selectedFilter, setValue]);

    const [timeRecords] = useFirebaseQueryOptions<TimeRecord>(
        `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.TIME_RECORDS}`,
        { queryConstraints: or(where('endAt', '==', null), where('endAt', '>=', startDate)) },
    );

    const filteredTimeRecords = useMemo(() => {
        if (!email) return timeRecords;
        return timeRecords.filter((record) => record.email === email);
    }, [timeRecords, email]);

    const table = useMemo(() => {
        const columns = buildTimesheetColumns(startDate, endDate, columnGrouping, text.weekShort);
        const groupOrder = groupBy === 'activity'
            ? [...sortArticles(activities).map(a => a.id!), UNASSIGNED_ACTIVITY_KEY]
            : [];
        return buildTimesheetTable(filteredTimeRecords, groupBy, columns, groupOrder);
    }, [startDate, endDate, columnGrouping, text.weekShort, filteredTimeRecords, groupBy, activities]);

    // Looked up from the live table so the modal reflects deletes while open.
    const selectedColumn = selectedCell ? table.columns[selectedCell.columnIndex] : undefined;
    const selectedCellRecords = selectedCell
        ? table.rows.find(row => row.key === selectedCell.key)?.cells[selectedCell.columnIndex]?.records ?? []
        : [];

    const activityName = (groupKey: string) => {
        if (groupKey === UNASSIGNED_ACTIVITY_KEY) return text.unassignedActivity;
        return activities.find(a => a.id === groupKey)?.name || groupKey;
    };

    const groupLabel = (groupKey: string) => {
        if (groupBy === 'deal') return <DealLink dealId={groupKey} path={PATHS.DEAL_TIMELINE} />;
        if (groupBy === 'person') return (
            <div className="d-flex align-items-center gap-2">
                <Gravatar identifier={groupKey} size="xxs" default="wavatar" />
                {groupKey}
            </div>
        );
        return activityName(groupKey);
    };

    const canEdit = (tr: TimeRecord) => orgRole !== 'worker' || tr.email === currentUser?.email;

    const handleDeleteTimeRecord = (tr: TimeRecord) => {
        confirm(async () => {
            try {
                await deleteDoc(doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.TIME_RECORDS}/${tr.id}`));
                toast.success(text.defaultSuccess);
            } catch {
                toast.error(text.defaultError);
            }
        });
    };

    const groupByLabel = groupBy === 'deal' ? text.deal : groupBy === 'person' ? text.person : text.activity;

    const columns: DataTableColumn<TimesheetRow>[] = [
        {
            label: groupByLabel,
            render: (row) => groupLabel(row.key)
        }
    ];

    for (let columnIndex = 0; columnIndex < table.columns.length; columnIndex++) {
        columns.push({
            align: 'center',
            label: table.columns[columnIndex].label,
            render: (row) => {
                const duration = row.cells[columnIndex].duration;
                if (!duration) return '—';

                return (
                    <span
                        role="button"
                        className="text-primary text-decoration-underline"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedCell({ key: row.key, columnIndex })}
                    >
                        {formatDurationFromMs(duration)}
                    </span>
                );
            }
        });
    }

    columns.push({
        align: 'center',
        label: text.totalHours,
        render: (row) => <strong>{formatDurationFromMs(row.total)}</strong>
    });

    async function getDealName(dealId: string): Promise<string> {
        const snap = await getDoc(doc(
            firestore,
            `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${dealId}`
        ));

        const deal = snap.data() as Deal | undefined;

        if (deal?.name) {
            return deal.name;
        }

        return dealId;
    }

    async function getGroupName(groupKey: string): Promise<string> {
        if (groupBy === 'deal') return getDealName(groupKey);
        if (groupBy === 'activity') return activityName(groupKey);
        return groupKey;
    }

    async function handleExportCsv() {
        const csvContent = await generateTimesheetCsv({
            table,
            groupByLabel,
            totalHoursLabel: text.totalHours,
            totalLabel: text.total,
            getGroupName
        });

        const blob = new Blob(['\uFEFF' + csvContent], {
            type: 'text/csv;charset=utf-8;'
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        const startDateLabel = formatDate(startDate, {
            isoDate: true,
            noTime: true
        });

        const endDateLabel = formatDate(endDate, {
            isoDate: true,
            noTime: true
        });

        link.href = url;
        link.download = `${text.timesheets}_${startDateLabel}_${endDateLabel}.csv`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    }

    const renderFooterCell = (col: DataTableColumn<TimesheetRow>, ix: number) => {
        if (ix === 0) return text.total;
        if (ix === columns.length - 1) return formatDurationFromMs(table.totals.total);

        const duration = table.totals.cells[ix - 1];
        return duration ? formatDurationFromMs(duration) : '—';
    };

    return (
        <>
            <PageHeader
                title={text.timesheets}
                actions={[
                    {
                        text: text.add,
                        icon: 'bi-plus-lg',
                        onClick: () => setShowNewEntryModal(true)
                    },
                    ...(isManager ? [{
                        text: text.createInvoice,
                        icon: 'bi-receipt',
                        onClick: () => setShowCreateInvoiceModal(true)
                    }] : []),
                    ...(isManager ? [{
                        text: text.articles,
                        icon: 'bi-gear',
                        link: PATHS.ARTICLES
                    }] : []),
                    {
                        text: text.exportCsv,
                        icon: 'bi-download',
                        onClick: handleExportCsv
                    }
                ]}
            />

            <div className="d-flex align-items-center gap-3 my-4 flex-wrap">
                <Form.Select
                    size='sm'
                    className="w-auto"
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value as TimePeriodFilter)}
                >
                    <option value="last7days">{text.last7Days}</option>
                    <option value="last30days">{text.last30Days}</option>
                    <option value="thisMonth">{text.thisMonth}</option>
                    <option value="lastMonth">{text.lastMonth}</option>
                    <option value="custom">{text.customDates}</option>
                </Form.Select>

                <Form.Select
                    size="sm"
                    className="w-auto"
                    value={columnGrouping}
                    onChange={(e) => setColumnGrouping(e.target.value as ColumnGrouping)}
                >
                    <option value="day">{text.perDay}</option>
                    <option value="week">{text.perWeek}</option>
                </Form.Select>

                {selectedFilter === 'custom' && (
                    <div className="d-flex align-items-center gap-2">
                        <span className="fw-semibold small">{text.start}</span>
                        <Form.Control
                            size='sm'
                            type="date"
                            {...register('startDate')}
                            isInvalid={!isDateRangeValid}
                        />

                        <span className="fw-semibold small">{text.end}</span>
                        <div className="position-relative d-flex flex-column">
                            <Form.Control
                                size='sm'
                                type="date"
                                {...register('endDate')}
                                isInvalid={!isDateRangeValid}
                            />
                            {!isDateRangeValid && (
                                <Form.Control.Feedback
                                    type="invalid"
                                    className="position-absolute top-100"
                                >
                                    {text.endDateAfterStartDate}
                                </Form.Control.Feedback>
                            )}
                        </div>
                    </div>
                )}

                <OrgMemberSelect
                    size='sm'
                    disabled={!isManager}
                    emptyLabel={text.allMembers}
                    value={email}
                    onChange={(val) => setEmail(val)}
                />

                <Form.Select
                    size='sm'
                    className="w-auto"
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value as GroupBy)}
                >
                    <option value="deal">{text.groupBy}: {text.deal}</option>
                    <option value="person">{text.groupBy}: {text.person}</option>
                    <option value="activity">{text.groupBy}: {text.activity}</option>
                </Form.Select>
            </div>

            <h5 className="mb-3">
                {formatDate(startDate, { noTime: true })} - {formatDate(endDate, { noTime: true })}
            </h5>

            <DataTable
                padding
                data={table.rows}
                columns={columns}
                footer={renderFooterCell}
            />

            {showNewEntryModal && <TimesheetEntryModal onClose={() => setShowNewEntryModal(false)} />}

            {showCreateInvoiceModal && (
                <CreateInvoiceModal
                    onClose={() => setShowCreateInvoiceModal(false)}
                    initialStartDate={startDate}
                    initialEndDate={endDate}
                />
            )}

            {selectedCell && selectedColumn && (
                <Modal show={true} onHide={() => setSelectedCell(null)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {formatDate(selectedColumn.dates[0], { noTime: true })}
                            {selectedColumn.dates.length > 1 && (
                                <> – {formatDate(selectedColumn.dates[selectedColumn.dates.length - 1], { noTime: true })}</>
                            )}
                            <div className="small text-muted fw-normal">
                                {groupLabel(selectedCell.key)}
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="d-flex flex-column gap-3">
                            {selectedCellRecords.map((record, index) => (
                                <div key={record.id} className={`d-flex justify-content-between align-items-start${index < selectedCellRecords.length - 1 ? ' border-bottom pb-2' : ''}`}>
                                    <div>
                                        <div className="fw-semibold">{formatDuration(record.startAt, record.endAt)}</div>
                                        <div className="small text-muted">
                                            {formatDate(record.startAt, { noYear: true })} – {record.endAt ? formatDate(record.endAt, { noYear: true }) : '…'}
                                        </div>
                                        {record.notes && <div className="small">{record.notes}</div>}
                                        {isManager && groupBy !== 'person' && <div className="small text-muted">{record.email}</div>}
                                        {groupBy !== 'deal' && <div className="small text-muted"><DealLink dealId={record.dealId} /></div>}
                                    </div>
                                    {canEdit(record) && (
                                        <div className="d-flex gap-1">
                                            <AdvancedButton
                                                variant="outline-secondary"
                                                size="sm"
                                                icon="bi-pencil"
                                                onClick={() => {
                                                    setSelectedCell(null);
                                                    setEditingRecord(record);
                                                }}
                                            />
                                            <AdvancedButton
                                                variant="outline-danger"
                                                size="sm"
                                                icon="bi-trash"
                                                onClick={() => handleDeleteTimeRecord(record)}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Modal.Body>
                </Modal>
            )}

            {editingRecord && (
                <TimesheetEntryModal
                    onClose={() => setEditingRecord(null)}
                    initialData={editingRecord}
                />
            )}
            <ConfirmModal />
        </>
    );
}